/**
 * Layout pieces of `/settings/tenant` (#2429): a section (heading, one-line
 * description, content), the bordered panel a section's content sits in, and
 * the panel's `label | value | action` row.
 *
 * Every panel row uses the same three columns, so the branding, integration
 * and operations panels line up. A row whose value is an input names its
 * label with `labelFor`; a read-only row's label is plain text, so it labels
 * nothing.
 */
import type { ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { Button, Card, Label } from '@resource-kit/ui/react';

export function SettingsSection({
  id,
  title,
  description,
  children,
}: {
  /** Anchor of the section nav: the element id is `tenant-section-<id>`. */
  id: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const sectionId = `tenant-section-${id}`;
  return (
    <section
      id={sectionId}
      data-testid={sectionId}
      aria-labelledby={`${sectionId}-title`}
      className="scroll-mt-6 space-y-4"
    >
      <div className="space-y-1">
        <h2 id={`${sectionId}-title`} className="text-base font-semibold tracking-tight">
          {title}
        </h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** The bordered panel of a section; its rows are separated by rules. */
export function SettingsPanel({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <Card data-testid={testId} className="divide-y overflow-hidden shadow-xs">
      {children}
    </Card>
  );
}

export function SettingsRow({
  label,
  labelFor,
  action,
  testId,
  children,
}: {
  label: ReactNode;
  /** The id of the input in the value cell, when there is one. */
  labelFor?: string;
  action?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="grid items-center gap-x-4 gap-y-2 px-5 py-3 sm:grid-cols-[200px_minmax(0,1fr)_auto]"
    >
      {labelFor ? (
        <Label htmlFor={labelFor} className="font-normal text-muted-foreground">
          {label}
        </Label>
      ) : (
        <span className="text-sm text-muted-foreground">{label}</span>
      )}
      <div className="min-w-0 text-sm">{children}</div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">{action}</div>
    </div>
  );
}

/** The Pencil button that opens a row (or a card) for editing. */
export function EditButton({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );
}
